import { Box, Radio, Stack } from '@mui/material';
import { PropsWithChildren } from 'react';
import {
  FieldArrayWithId,
  FieldValues,
  UseFieldArrayReturn,
} from 'react-hook-form';

import { FormAwareCardRepeaterProps } from '@/components/form/formAwareInputs/FormAwareCardRepeater/FormAwareCardRepeater';
import {
  AdditionalItemRendererProps,
  FormAwareInputItemDuplicateResolutionProps,
} from '@/components/form/formAwareInputs/FormAwareInput.types';
import { Card, CardVariant } from '@/components/layout/Card/Card';
import { ArrayFieldNameFromFormShape } from '@/types/react-hook-form';

export interface CardFieldItemProps<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
> {
  i: number;
  value: FieldArrayWithId<
    FormShape,
    ArrayFieldNameFromFormShape<FormShape>,
    FieldArrayKeyName
  >;
  render: FormAwareCardRepeaterProps<FormShape, FieldArrayKeyName>['render'];
  fieldArrayProps: Pick<
    UseFieldArrayReturn<
      FormShape,
      ArrayFieldNameFromFormShape<FormShape>,
      FieldArrayKeyName
    >,
    'swap' | 'fields' | 'remove'
  >;
  variant: CardVariant;
  spacing: number;
  fieldArrayKeyName: FieldArrayKeyName;
  additionalItemRendererProps?: AdditionalItemRendererProps;
}

/**
 * A single card item of a FormAwareCardRepeater.
 */
export function CardFieldItem<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
>({
  i,
  value,
  render,
  fieldArrayProps: { swap, fields, remove },
  variant,
  spacing,
  fieldArrayKeyName,
  additionalItemRendererProps,
}: CardFieldItemProps<FormShape, FieldArrayKeyName>) {
  return (
    <WrappedCardFieldItem
      duplicateResolutionConfigs={
        additionalItemRendererProps?.duplicateResolutionConfigs
      }
    >
      <Card
        key={value[fieldArrayKeyName]}
        variant={variant}
        sx={{
          mb: spacing,
          borderRadius: '6px',
        }}
        data-testid="repeater-input-item"
      >
        {render(
          i,
          { swap, fields, remove },
          value[fieldArrayKeyName],
          additionalItemRendererProps
        )}
      </Card>
    </WrappedCardFieldItem>
  );
}

function WrappedCardFieldItem({
  children,
  duplicateResolutionConfigs,
}: PropsWithChildren<{
  duplicateResolutionConfigs:
    | FormAwareInputItemDuplicateResolutionProps
    | undefined;
}>) {
  if (!duplicateResolutionConfigs) {
    return <>{children}</>;
  }

  const { isSelected, onSelect } = duplicateResolutionConfigs;

  return (
    <Stack direction="row" alignItems="center">
      <Box>
        <Radio
          onChange={(_, checked) => {
            if (checked) {
              onSelect();
            }
          }}
          checked={isSelected}
        />
      </Box>
      <Box flexGrow={1}>{children}</Box>
    </Stack>
  );
}
